import React, { useState, useContext, useMemo, useEffect } from "react";
import AdvancedSearchContext from "context/AdvancedSearchContext";
import _ from "lodash";
import styled from "styled-components";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import CreateMarket from "./CreateMarket";
import UserService from "services/UserService";
import axios from "axios";
import { getMarkets } from "services/ConfigServices";
import ExportTableToCSV from "components/ExportTableToCSV/ExportTableToCSV";
import { markets } from "variables/markets";
import { statusData, energyData } from "variables/lists/markets";
import moment from "moment";
import CustomSwitch from "components/Input/CustomSwitch.js";

const UploadButton = styled.div`
  margin-left: 0px;
  &:hover {
    cursor: pointer;
  }
`;

export default function useMarketsTable() {
  const permis = JSON.parse(localStorage.getItem("permission")) || [];
  const marketadmin = permis.includes("markets_admin");
  const appContext = useContext(AdvancedSearchContext);
  const { chartIdGroup, groupInfoListById } = appContext;
  const [totalRows, setTotalRows] = useState(0);
  const [refresh, setRefresh] = useState(false);
  const url = getMarkets(chartIdGroup);
  const [results, setResults] = useState({});
  const [loading, setLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedMarkets, setSelectedMarkets] = useState([]);
  const [hideclosed, setHideclosed] = useState(true);

  const refreshTable = () => {
    setRefresh(!refresh);
  }
  const handleHideclosedChange = (event) => {
    setHideclosed(!hideclosed);
  };

  let prevIdGroup = ["0"];
  let prevRefresh = true;
  useEffect(() => {
    if (chartIdGroup && chartIdGroup[0] !== "0") {
      if ((prevIdGroup && prevIdGroup === chartIdGroup)
        && (prevRefresh && prevRefresh === refresh)) {
        return;
      }
      //setLoading(true);
      async function fetchData() {
        const response = await axios.get(
          url,
          {
            headers: {
              Authorization: `Bearer ${UserService.getToken()}`,
            },
          }
        );
        setResults(response.data);
        setLoading(false);
      }
      prevRefresh = refresh;
      prevIdGroup = chartIdGroup;
      fetchData();
    }
  }, [chartIdGroup, refresh]);

  const [filterText, setFilterText] = useState("");
  const [resetPaginationToggle, setResetPaginationToggle] = useState(false);

  const SearchComponent = ({ filterText, onFilter, onClear }) => (
    <>
      <div style={{ marginTop: "-7px" }}>
        <TextField
          type="search"
          variant="outlined"
          margin="normal"
          size="small"
          autoFocus="true"
          value={filterText}
          onChange={onFilter}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </div>
    </>
  );


  const UploadComponent = ({ onUpload }) => (
    <>
      <UploadButton type="button" onClick={onUpload}>
        <CreateMarket refetch={refreshTable} onClick={onUpload} />
      </UploadButton>
    </>
  );

  const filteredItems = _.get(results, `markets`, []).filter(
    (item) =>
      ((item.parameters.name && item.parameters.name.toLowerCase().includes(filterText.toLowerCase())) ||
      (item.parameters.marketnumber && item.parameters.marketnumber.toLowerCase().includes(filterText.toLowerCase())) ||
      (item.parameters.groups[0] && groupInfoListById["" + item.parameters.groups[0]] && groupInfoListById["" + item.parameters.groups[0]].toLowerCase().includes(filterText.toLowerCase())) ||
      (item.parameters.status && statusData.find((x) => x.id == item.parameters.status) && statusData.find((x) => x.id == item.parameters.status).name.toLowerCase().includes(filterText.toLowerCase())) ||
      (item.parameters.energy && energyData.find((x) => x.id == item.parameters.energy) && energyData.find((x) => x.id == item.parameters.energy).name.toLowerCase().includes(filterText.toLowerCase())) ||
      (item.parameters.datebegin && moment(item.parameters.datebegin).isValid() && moment(item.parameters.datebegin).format("DD/MM/YYYY").includes(filterText)) ||
      (item.parameters.dateend && moment(item.parameters.dateend).isValid() && moment(item.parameters.dateend).format("DD/MM/YYYY").includes(filterText))) && (!hideclosed || item.parameters.status === 'inprogress')
  );

  const subHeaderComponentMemo = useMemo(() => {
    const handleClear = () => {
      if (filterText) {
        setResetPaginationToggle(!resetPaginationToggle);
        setFilterText("");
      }
    };

    const onFilterText = (e) => setFilterText(e.target.value);
    let exportarray = selectedMarkets.map((item) => {
      let markettoexport = {};
      markettoexport[markets.general.name] = item.parameters.name;
      markettoexport[markets.general.status] = statusData.find((x) => x.id == item.parameters.status)?.name;
      markettoexport[markets.general.datebegin] = (item.parameters.datebegin && moment(item.parameters.datebegin).isValid()) ? moment(item.parameters.datebegin).format("DD/MM/YYYY") : "";
      markettoexport[markets.general.dateend] = (item.parameters.dateend && moment(item.parameters.dateend).isValid()) ? moment(item.parameters.dateend).format("DD/MM/YYYY") : "";
      markettoexport[markets.general.energy] = energyData.find((x) => x.id == item.parameters.energy)?.name;
      markettoexport[markets.membershipcount] = item.membershipcount;
      markettoexport[markets.general.groups] = (item.parameters.groups[0] && groupInfoListById["" + item.parameters.groups[0]]) ? groupInfoListById["" + item.parameters.groups[0]] : "";
      return markettoexport;
    });

    return (
      <>
        <CustomSwitch
          style={{ margin: "10px 40px 0 3px" }}
          inputTitle={markets.general.hideclosed}
          checkedState={hideclosed}
          handleSwitchChange={handleHideclosedChange}
        />
        <SearchComponent
          key="SearchComponent"
          onFilter={onFilterText}
          onClear={handleClear}
          filterText={filterText}
        />
        {marketadmin && (
          <UploadButton type="button" onClick={handleClear}>
            <CreateMarket refetch={refreshTable} onClick={handleClear} />
          </UploadButton>
        )}
        <UploadButton type="button" onClick={handleClear}>
          <ExportTableToCSV dataarray={exportarray} disabled={!selectedMarkets || selectedMarkets.length < 1} />
        </UploadButton>
      </>
    );
  }, [filterText, resetPaginationToggle, selectedMarkets, hideclosed]);

  return {
    gridState: {
      defaultPageSize: 10,
      data: filteredItems,
      paginationTotalRows: totalRows,
      filterable: true,
      manual: true,
      paginationResetDefaultPage: resetPaginationToggle,
      subHeaderComponent: subHeaderComponentMemo,
      loading,
    },
    refreshTable,
    setSelectedMarkets
  };
}
